<template>
  <!-- Loading -->
  <div v-if="isLoading && swaps.length === 0" class="p-6 text-center">
    <div
      class="w-6 h-6 border-2 border-[#1980e6] border-t-transparent rounded-full animate-spin mx-auto mb-2"
    />
    <p class="text-xs text-gray-500">Loading swap history...</p>
  </div>

  <!-- Empty State -->
  <div v-else-if="swaps.length === 0" class="p-6 text-center">
    <p class="text-xs text-gray-500">No swaps yet. Your swap history will appear here.</p>
  </div>

  <!-- Swap List -->
  <div v-else class="p-3 space-y-3">
    <div
      v-for="swap in swaps"
      :key="swap.id"
      class="rounded-xl bg-gray-50 p-4 transition-all"
      :class="isActive(swap.status) && 'ring-1 ring-[#1980e6]/20'"
    >
      <!-- Swap Card -->
      <button type="button" class="w-full text-left" @click="toggle(swap.id)">
        <!-- Route first; full source amount stays intact directly underneath. -->
        <div class="mb-2">
          <div class="flex items-start justify-between gap-2">
            <span
              data-testid="swap-route"
              class="text-base text-[#0e141b] font-medium min-w-0 break-words"
            >
              {{ swap.source_token_symbol }}
              <span class="mx-1">&rarr;</span>
              SPOTTY
            </span>
            <span
              class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0"
              :class="statusBadge(swap.status)"
            >
              <!-- Status icon -->
              <svg
                v-if="isActive(swap.status)"
                class="w-3.5 h-3.5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="3"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <svg
                v-else-if="swap.status === 'completed'"
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <svg
                v-else-if="swap.status === 'failed'"
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {{ statusLabel(swap) }}
            </span>
          </div>
          <span
            data-testid="swap-source-amount"
            class="block text-sm font-medium text-[#0e141b] mt-0.5 break-words"
          >
            <span class="whitespace-nowrap">
              {{ formatSourceAmount(swap.source_amount) }} {{ swap.source_token_symbol }}
            </span>
          </span>
        </div>

        <div
          class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm text-gray-500"
        >
          <span v-if="swap.source_amount_usd" class="whitespace-nowrap">
            {{ swap.source_amount_usd }}
          </span>
          <span class="whitespace-nowrap ml-auto">
            {{ formatDate(swap.created_at) }}
          </span>
        </div>
      </button>

      <!-- Resume destination swap button for interrupted swaps -->
      <div v-if="isResumable(swap)" class="mt-2 px-1">
        <button
          type="button"
          class="w-full py-2 rounded-lg text-sm font-medium transition-colors"
          :class="
            resumingId === swap.id || preparationState(swap) !== 'ready'
              ? 'bg-blue-100 text-gray-500 cursor-wait'
              : 'bg-[#1980e6] text-white hover:bg-[#166ec5]'
          "
          :disabled="resumingId === swap.id || preparationState(swap) !== 'ready'"
          @click.stop="handleResume(swap)"
        >
          <template v-if="resumingId === swap.id">
            <svg class="w-4 h-4 animate-spin inline-block mr-1.5" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="3"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Completing...
          </template>
          <template v-else-if="isResumable(swap) && preparationState(swap) !== 'ready'">
            Preparing transaction…
          </template>
          <template v-else>
            {{
              isFailedFinalRetry(swap)
                ? 'Retry final swap'
                : isFailedIntermediateRetry(swap)
                  ? 'Retry GRAM swap'
                  : atFinalSwapStep(swap)
                    ? 'Continue to final swap'
                    : continuationStepLabel(swap)
            }}
          </template>
        </button>
      </div>

      <!-- Expanded swap details -->
      <RecentSwapDetails v-if="expandedId === swap.id" :swap="swap" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  statusBadge,
  statusLabel,
  formatDate,
  isResumable,
  atFinalSwapStep,
  continuationStepLabel,
  type RecentSwap,
} from './presentation'
import RecentSwapDetails from './RecentSwapDetails.vue'
import type { RecentSwapContinuationStates } from './use-recent-swap-continuation'

const props = defineProps<{
  swaps: RecentSwap[]
  isLoading: boolean
  continuationStates?: RecentSwapContinuationStates
}>()

const emit = defineEmits<{
  resumeTonLeg: [swap: RecentSwap]
}>()

const resumingId = ref<string | null>(null)

const handleResume = (swap: RecentSwap) => {
  if (preparationState(swap) !== 'ready') return
  resumingId.value = swap.id
  emit('resumeTonLeg', swap)
}

const resetResuming = () => {
  resumingId.value = null
}

// If the swap being resumed is no longer resumable (status moved to completed/failed
// or disappeared from the list), clear the button state so it doesn't stay stuck
// on "Completing…" forever when the resume call threw (e.g. wallet disconnected).
watch(
  () => props.swaps,
  (swaps) => {
    if (!resumingId.value) return
    const current = swaps.find((s) => s.id === resumingId.value)
    if (!current || !isResumable(current)) {
      resumingId.value = null
    }
  },
  { deep: true }
)

defineExpose({ resetResuming })

const ACTIVE_STATUSES = new Set(['pending', 'bridging', 'swapping', 'needs_attention'])
const isActive = (status: string) => ACTIVE_STATUSES.has(status)
const preparationState = (swap: RecentSwap) => props.continuationStates?.[swap.id] ?? 'preparing'
const isFailedTonRetry = (swap: RecentSwap, type: 'usdt_to_ton' | 'ton_to_spotty'): boolean => {
  const firstIncomplete = swap.progress.steps.find(
    (step) => step.status !== 'completed' && step.status !== 'cancelled'
  )
  return (
    firstIncomplete?.type === type &&
    firstIncomplete.status === 'failed' &&
    firstIncomplete.failure_kind !== 'terminal'
  )
}
const isFailedFinalRetry = (swap: RecentSwap): boolean => isFailedTonRetry(swap, 'ton_to_spotty')
const isFailedIntermediateRetry = (swap: RecentSwap): boolean =>
  isFailedTonRetry(swap, 'usdt_to_ton')

const formatSourceAmount = (value: string): string => {
  const [integer = '', fraction] = value.split('.')
  const firstGroupLength = integer.length % 3 || 3
  const groups = [integer.slice(0, firstGroupLength)]
  for (let index = firstGroupLength; index < integer.length; index += 3) {
    groups.push(integer.slice(index, index + 3))
  }
  const grouped = groups.join(',')
  return fraction === undefined ? grouped : `${grouped}.${fraction}`
}

const expandedId = ref<string | null>(null)
const toggle = (id: string) => {
  expandedId.value = expandedId.value === id ? null : id
}
</script>
